import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { getStopDatePickerProps } from '@folio/utils/stop-reasons';
import { PolicyTimeZoneProvider } from '@folio/utils/time-zone';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyStopReasons } from '@folio/utils/usePolicyStopReasons';
import { useStopDateError } from '@folio/utils/useStopDateError';
import { InfoIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { FunnelStepHeader } from '../../components/layout/FunnelStepHeader';
import { DateTimeInput } from '../../components/forms/date-time-input';
import { StepperFooter } from '../../components/navigation/stepper-footer/stepper-footer';
import { Alert } from '../../components/ui/alert';
import { Card, CardContent } from '../../components/ui/card';
import { formatDate } from '../../lib/date-formatting';
import { isEffectiveDateComplete, resolveEffectiveDate } from './effective-date';
import { StopFunnelGuard } from './StopFunnelGuard';
import { useStopDraft } from './useStopDraft';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function EffectiveDateStep({
  firstStepPath,
  recapPath,
}: {
  firstStepPath: string;
  recapPath: string;
}) {
  return (
    <PolicyTimeZoneProvider>
      <StopFunnelGuard firstStepPath={firstStepPath}>
        {draft => (
          <EffectiveDate
            reason={draft.reason}
            initialStopDate={draft.stopDate}
            requiresDocuments={draft.requiredDocuments.length > 0}
            recapPath={recapPath}
          />
        )}
      </StopFunnelGuard>
    </PolicyTimeZoneProvider>
  );
}

function EffectiveDate({
  reason,
  initialStopDate,
  requiresDocuments,
  recapPath,
}: {
  reason: string;
  initialStopDate?: string;
  requiresDocuments: boolean;
  recapPath: string;
}) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const { next } = useStepperContext();
  const navigate = useNavigate();
  const params = useParams();
  const { save } = useStopDraft(policy.id);
  const [stopDate, setStopDate] = useState(initialStopDate);
  const [saving, setSaving] = useState(false);

  const { stopReasonConfigs } = usePolicyStopReasons({
    productId: policy.productId,
    policy: {
      startedAt: policy.startedAt,
      nextRenewalAt: policy.nextRenewalAt,
      suspendedAt: policy.suspendedAt,
    },
  });

  const reasonConfig = stopReasonConfigs.find(config => config.reason === reason);
  const effectiveDate = resolveEffectiveDate({ reason: reasonConfig, policy, stopDate });
  const pastOnly =
    !!reasonConfig && effectiveDate.kind === 'CHOSEN' && !reasonConfig.appliedInFutureAllowed;
  const stopDateError = useStopDateError(policy)({
    reason: reasonConfig,
    stopDate: effectiveDate.kind === 'CHOSEN' ? effectiveDate.at : undefined,
  });

  // Waiting on the config is not the same as a reason that takes no date: saving before it resolves
  // writes the date under the wrong key, and the confirmation then rejects it.
  const goNext = async () => {
    if (!reasonConfig || !effectiveDate.at || stopDateError) {
      return;
    }
    setSaving(true);
    try {
      // Same body the recap confirms with: the backend reads the date under the reason's own key.
      await save({
        reason,
        details: [{ key: reasonConfig.appliedAtDate ?? 'stopDate', value: effectiveDate.at }],
      });
      // The documents step has nothing to ask for on most reasons; stepping over it beats showing
      // an empty one. Back from the recap still lands on it, saying as much.
      if (requiresDocuments) {
        next();
      } else {
        navigate(generatePath(recapPath, params));
      }
    } catch {
      // Stay on the step; the shared MutationCache already shows the API error.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={t('stopFunnel.effectiveDate.title')}
        subtitle={pastOnly ? undefined : t('stopFunnel.effectiveDate.subtitle')}
      />

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          {effectiveDate.kind === 'CHOSEN' ? (
            <Stack gap={1}>
              <DateTimeInput
                label={t('stopFunnel.effectiveDate.label')}
                placeholder={t('stopFunnel.effectiveDate.placeholder')}
                value={stopDate}
                onChange={setStopDate}
                {...getStopDatePickerProps({ reason: reasonConfig, policy })}
              />
              {stopDateError && (
                <Text size="sm" tone="destructive" role="alert">
                  {stopDateError}
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm">
              {t(
                [
                  `${policy.productId}.stopFunnel.effectiveDate.${effectiveDate.kind}`,
                  `stopFunnel.effectiveDate.${effectiveDate.kind}`,
                ],
                { date: effectiveDate.at ? formatDate(new Date(effectiveDate.at)) : undefined },
              )}
            </Text>
          )}

          <Alert variant="info" className="flex items-start gap-2">
            <InfoIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
            <Text inline size="sm">
              {t(
                pastOnly
                  ? 'stopFunnel.effectiveDate.undoNoticeNoFuture'
                  : 'stopFunnel.effectiveDate.undoNotice',
              )}
            </Text>
          </Alert>
        </CardContent>
      </Card>

      <StepperFooter
        onNext={goNext}
        nextButtonEnabled={
          !!reasonConfig && isEffectiveDateComplete(effectiveDate) && !stopDateError
        }
        loading={saving}
      />
    </Stack>
  );
}
